// Deployment config. Two layers (owner decision 2026-09-30, legacy-migration
// RULES.md "Fizetési rendszer (feesubscription): párhuzamos teszt": the new
// payment system first runs IN PARALLEL with the live legacy one, as
// beta.feesubscription.com -> beta-api.feesubscription.com, and the cutover to
// secure.feesubscription.com -> api.feesubscription.com must need no rebuild;
// same pattern as secure.grabbitbot.com/src/config.ts):
//
// 1. RUNTIME: `/runtime-config.js` sets `window.__FEESUBSCRIPTION_CONFIG__`
//    before the bundle runs. In the Docker image entrypoint.sh writes it from
//    the container environment (BACKOFFICE_API_BASE_URL,
//    BACKOFFICE_ENVIRONMENT — validated by runtime-config.sh, refused when
//    missing); the cutover edits the host environment file and restarts. In
//    `vite dev` / `vite preview` the static public/runtime-config.js sets
//    nothing.
// 2. BUILD: the Vite env vars (README "Configuration"), then the default.
//
// `||`, not `??`: docker-compose passes every unset build arg as an EMPTY
// string (`${VAR:-}`), and the Dockerfile turns it into `ENV VAR=` — Vite
// then sees "" which `??` would keep. An empty value must mean "use the next
// layer".

/** Which deployment this is. `production` is the ONLY value that hides the
 * TEST / BETA banner (and even then not on a `beta.` host, see
 * `isTestDeployment`). */
export type DeploymentEnvironment = 'production' | 'beta'

export interface RuntimeConfig {
  apiBaseUrl?: string
  environment?: string
}

export interface BuildEnv {
  VITE_API_BASE_URL?: string
  VITE_AUTH0_LOGIN_URL?: string
  VITE_AUTH0_LOGIN_ORIGIN?: string
  VITE_SEND_IDEMPOTENCY_KEY?: string
}

export interface ResolvedConfig {
  apiBaseUrl: string
  auth0LoginUrl: string
  auth0LoginExpectedOrigin: string
  /** `null` = no runtime config (vite dev / preview / a test). */
  environment: DeploymentEnvironment | null
  sendIdempotencyKey: boolean
}

export const DEFAULT_API_BASE_URL = 'https://api.feesubscription.com'

const OFF_WORDS = ['0', 'false', 'no', 'off']

/** `VITE_SEND_IDEMPOTENCY_KEY`: ON unless explicitly switched off. Ticket
 * b82fc37e: api.feesubscription.com 0.9.0 reads the caller `Idempotency-Key`
 * on startonline AND lists it in the CORS allow-headers (app/main.py:
 * `configure_cors(..., allow_headers=(*DEFAULT_ALLOW_HEADERS,
 * "Idempotency-Key"))`), so the preflight passes. The off switch stays for
 * a rollback to a backend that does not allow the header. */
export function parseSendIdempotencyKey(value: string | undefined): boolean {
  const v = String(value || '').trim().toLowerCase()
  return !OFF_WORDS.includes(v)
}

function parseEnvironment(value: string | undefined): DeploymentEnvironment | null {
  if (value === 'production' || value === 'beta') return value
  // Anything else that arrived at runtime is treated as a TEST deployment:
  // an unknown label must never hide the banner.
  return value ? 'beta' : null
}

export function resolveConfig(runtime: RuntimeConfig | undefined, env: BuildEnv): ResolvedConfig {
  const rt = runtime ?? {}
  const apiBaseUrl = (rt.apiBaseUrl || env.VITE_API_BASE_URL || DEFAULT_API_BASE_URL).replace(/\/+$/, '')
  // A runtime API URL wins over the build-time login overrides: those would
  // otherwise keep pointing the login popup at the name the image was built for.
  const auth0LoginUrl = (rt.apiBaseUrl ? '' : env.VITE_AUTH0_LOGIN_URL) || `${apiBaseUrl}/user/login/auth0`
  const auth0LoginExpectedOrigin =
    (rt.apiBaseUrl ? '' : env.VITE_AUTH0_LOGIN_ORIGIN) || new URL(apiBaseUrl).origin
  return {
    apiBaseUrl,
    auth0LoginUrl,
    auth0LoginExpectedOrigin,
    environment: parseEnvironment(rt.environment),
    sendIdempotencyKey: parseSendIdempotencyKey(env.VITE_SEND_IDEMPOTENCY_KEY),
  }
}

/** Whether the TEST / BETA banner must show. Only a runtime-configured
 * `production` deployment hides it, and never on a host whose name starts
 * with `beta.` or talks to a `beta-api.` backend — a mis-set environment
 * label can therefore not make the beta system look like the live one. A
 * missing runtime config (vite dev / preview) also shows it. */
export function isTestDeployment(cfg: Pick<ResolvedConfig, 'environment' | 'apiBaseUrl'>, hostname: string): boolean {
  if (/^beta[.-]/i.test(hostname)) return true
  let apiHost: string
  try {
    apiHost = new URL(cfg.apiBaseUrl).hostname
  } catch {
    return true
  }
  if (/^beta[.-]/i.test(apiHost)) return true
  return cfg.environment !== 'production'
}

declare global {
  interface Window {
    __FEESUBSCRIPTION_CONFIG__?: RuntimeConfig
  }
}

const resolved = resolveConfig(
  typeof window !== 'undefined' ? window.__FEESUBSCRIPTION_CONFIG__ : undefined,
  import.meta.env,
)

/** Backend base URL — legacy `environment.apiUrl` (`https://api.feesubscription.com`
 * in the legacy production environment, audit §1), now the FastAPI port:
 * beta-api.feesubscription.com on staging, api.feesubscription.com after the
 * cutover. */
export const API_BASE_URL: string = resolved.apiBaseUrl

/** Auth0 login popup entry point — `GET /user/login/auth0` on the backend
 * (owner decision 2026-09-27: the back-office logs in through the Auth0
 * "Payment" application, BFF pattern — the backend does the code exchange,
 * its callback is `<api>/user/login/auth0/callback`, and it `postMessage`s its
 * OWN session token to this origin only). This replaces the legacy password
 * form and the hard-coded `https://api.feesubscription.com/user/login/google`
 * popup (audit §10 D5). */
export const AUTH0_LOGIN_URL: string = resolved.auth0LoginUrl

/** Origin the Auth0 popup's `postMessage` reply must come from — the
 * backend's own origin (the popup page is served by the backend). */
export const AUTH0_LOGIN_EXPECTED_ORIGIN: string = resolved.auth0LoginExpectedOrigin

/** Send the caller `Idempotency-Key` header on `POST …/payment/startonline`
 * (BubbleTicket 0eb1271b, switched ON by default in 0.2.0 — b82fc37e, see
 * `parseSendIdempotencyKey`). The key itself is always generated and kept per
 * submit (see src/lib/useMoneyAction.ts). */
export const SEND_IDEMPOTENCY_KEY: boolean = resolved.sendIdempotencyKey

/** The deployment label from the runtime config (`null` without one). */
export const DEPLOYMENT_ENVIRONMENT: DeploymentEnvironment | null = resolved.environment

/** TEST / BETA banner on (see `isTestDeployment`). */
export const IS_TEST_DEPLOYMENT: boolean = isTestDeployment(
  resolved,
  typeof window !== 'undefined' ? window.location.hostname : '',
)

/** Telegram bot the Login Widget on `/connections/telegram` signs in with
 * (legacy `data-telegram-login`; its login domain is configured at
 * BotFather to this frontend's host). */
export const TELEGRAM_CONNECT_BOT: string = import.meta.env.VITE_TELEGRAM_CONNECT_BOT || 'FeeSubscriptionConnectBot'

/** App build version, injected by Vite from package.json at build time. */
export const APP_VERSION: string = __APP_VERSION__

/** Legacy `''` route redirected to `/projects` (audit §3, last row). */
export const POST_LOGIN_PATH = '/projects'

/** Legacy `environment.title`, set by every component via `Title.setTitle`. */
export const APP_TITLE = 'Fee Subscription'
